// ═══════════════════════════════════════════════════════════════
//  interventionsPlanning.js — « Mettre au planning » une intervention (§8.2)
//
//  ⚠️ PUR : n'importe que taches/versPlanning.js (lui-même sans import),
//  extension `.js` obligatoire — interventionsPlanning.test.js le charge en
//  `node` pur.
//
//  Une intervention planifiée = une ou plusieurs lignes de
//  `planningCreneaux.taches[]` portant `interventionRefId` (et, en mode « À
//  l'heure », `heureDebut` / `heureFin`). Même motif que la tâche (T11) :
//  lien porté par le créneau, on AJOUTE aux lignes existantes, on n'écrase
//  jamais une demi-journée déjà planifiée. L'écriture elle-même passe par
//  affectedCreneauPayload (planningWrites.js), dans une transaction.
//
//  Validation L9 du planning ≠ réalisation de l'intervention : deux
//  workflows distincts, rien ne se propage de l'un à l'autre.
// ═══════════════════════════════════════════════════════════════
import { ROLES_PLANIFICATEURS, peutPlanifier } from "../taches/versPlanning.js";

export { ROLES_PLANIFICATEURS, peutPlanifier };

export const MODES = ["AM", "PM", "JOUR", "HEURE"];
export const MODE_LABEL = { AM: "Matin", PM: "Après-midi", JOUR: "Journée", HEURE: "À l'heure" };
export const HEURE_MIN = "07:00";
export const HEURE_MAX = "19:00";
const MIDI = 12 * 60;
const LABEL_MAX = 80;

// ─── Heures « HH:MM », minutes 00 ou 30 ────────────────────────
export function heureValide(h) {
  return typeof h === "string" && /^([01]\d|2[0-3]):(00|30)$/.test(h);
}
export function enMinutes(h) {
  if (!heureValide(h)) return null;
  const [a, b] = h.split(":").map(Number);
  return a * 60 + b;
}
export function depuisMinutes(m) {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
// Heures proposées, pas de 30 min, bornes comprises.
export function heuresPossibles(min = HEURE_MIN, max = HEURE_MAX) {
  const out = [];
  for (let m = enMinutes(min); m <= enMinutes(max); m += 30) out.push(depuisMinutes(m));
  return out;
}
// « 9 h 30–11 h »
export function libelleHeure(h) {
  const m = enMinutes(h);
  if (m == null) return "";
  const min = m % 60;
  return `${Math.floor(m / 60)} h${min ? ` ${String(min).padStart(2, "0")}` : ""}`;
}
export function libelleHoraire(debut, fin) {
  if (!heureValide(debut) || !heureValide(fin)) return "";
  return `${libelleHeure(debut)}–${libelleHeure(fin)}`;
}

// Début < 12:00 → matin, sinon après-midi.
export function periodeDeHeure(h) {
  const m = enMinutes(h);
  if (m == null) return null;
  return m < MIDI ? "AM" : "PM";
}

// Un horaire qui franchit midi est coupé en deux : [début, 12:00] le matin,
// [12:00, fin] l'après-midi. Horaire invalide (fin ≤ début) → [].
export function decouperHoraire(debut, fin) {
  const d = enMinutes(debut), f = enMinutes(fin);
  if (d == null || f == null || f <= d) return [];
  if (d < MIDI && f > MIDI) {
    return [
      { periode: "AM", heureDebut: debut, heureFin: "12:00" },
      { periode: "PM", heureDebut: "12:00", heureFin: fin },
    ];
  }
  return [{ periode: d < MIDI ? "AM" : "PM", heureDebut: debut, heureFin: fin }];
}

// ─── Dates (UTC : aucune dérive de fuseau) ─────────────────────
const JOURS_COURTS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
function enDate(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function enIso(dt) {
  return dt.toISOString().slice(0, 10);
}
export function dateValide(iso) {
  return typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso) && enIso(enDate(iso)) === iso;
}
export function jourSemaine(iso) { return enDate(iso).getUTCDay(); }
export function estWeekEnd(iso) { const j = jourSemaine(iso); return j === 0 || j === 6; }
// Index de colonne du planning (0 = lundi … 4 = vendredi), -1 le week-end.
export function indexJour(iso) { const j = jourSemaine(iso); return j >= 1 && j <= 5 ? j - 1 : -1; }
function jourSuivant(iso) { const dt = enDate(iso); dt.setUTCDate(dt.getUTCDate() + 1); return enIso(dt); }

// n demi-journées à partir de (date, periode), samedi et dimanche sautés.
// Un départ en week-end glisse au lundi matin.
export function enchainerDemiJournees(dateISO, periode, n) {
  const out = [];
  if (!dateValide(dateISO)) return out;
  let date = dateISO, p = periode === "PM" ? "PM" : "AM";
  while (estWeekEnd(date)) { date = jourSuivant(date); p = "AM"; }
  const total = Math.max(1, Math.floor(Number(n) || 1));
  while (out.length < total) {
    out.push({ date, periode: p });
    if (p === "AM") p = "PM";
    else {
      p = "AM";
      do { date = jourSuivant(date); } while (estWeekEnd(date));
    }
  }
  return out;
}

export function libelleJour(iso) {
  const dt = enDate(iso);
  return `${JOURS_COURTS[dt.getUTCDay()]} ${dt.getUTCDate()}`;
}
export function libelleSlot(s) {
  if (!s) return "";
  const base = libelleJour(s.date);
  if (heureValide(s.heureDebut) && heureValide(s.heureFin)) return `${base}, ${libelleHoraire(s.heureDebut, s.heureFin)}`;
  return `${base} ${s.periode === "PM" ? "après-midi" : "matin"}`;
}
// « lun. 2 matin → mer. 4 matin »
export function resumeSlots(slots) {
  const l = Array.isArray(slots) ? slots : [];
  if (!l.length) return "";
  if (l.length === 1) return libelleSlot(l[0]);
  const premier = l[0], dernier = l[l.length - 1];
  if (heureValide(premier.heureDebut) && premier.date === dernier.date) {
    return `${libelleJour(premier.date)}, ${libelleHoraire(premier.heureDebut, dernier.heureFin)}`;
  }
  return `${libelleSlot(premier)} → ${libelleSlot(dernier)}`;
}

// ─── Créneaux visés par un choix ───────────────────────────────
// choix = { date, mode: AM|PM|JOUR|HEURE, heureDebut, heureFin }
// Demi-journées : si le temps estimé est en demi-journées (> 1), elles
// s'enchaînent à partir de la date. « Journée » = au moins 2.
// → [{ date, periode, heureDebut, heureFin }]  (heures null hors HEURE)
// `ligneDetail` (mode « Ligne par ligne ») : ses demi-journées remplacent
// le temps estimé de l'intervention.
//
// Forme MULTI-JOURS (§3) : choix.dates = [AAAA-MM-JJ…] — le mode et
// l'horaire s'appliquent à CHAQUE jour (Matin, Après-midi, Journée = AM +
// PM, À l'heure coupé à midi), sans enchaînement. Jours invalides et
// week-ends ignorés (le planning couvre le lundi au vendredi), doublons
// retirés, ordre chronologique. La forme `date` seule reste acceptée.
export function slotsDepuisChoix(intervention, choix, ligneDetail = null) {
  if (Array.isArray(choix?.dates)) return slotsDesJours(choix);
  const date = choix?.date;
  if (!dateValide(date) || estWeekEnd(date)) return [];
  const mode = choix?.mode;
  if (mode === "HEURE") {
    return decouperHoraire(choix.heureDebut, choix.heureFin).map((p) => ({ date, ...p }));
  }
  if (!["AM", "PM", "JOUR"].includes(mode)) return [];
  let demis = 1;
  if (ligneDetail) {
    demis = Math.max(1, Math.round(Number(ligneDetail.demiJournees) || 1));
  } else {
    const t = intervention?.tempsEstime;
    demis = t?.unite === "demi" && Number(t.valeur) >= 1 ? Math.round(Number(t.valeur)) : 1;
  }
  const n = mode === "JOUR" ? Math.max(2, demis) : demis;
  return enchainerDemiJournees(date, mode === "PM" ? "PM" : "AM", n)
    .map((s) => ({ ...s, heureDebut: null, heureFin: null }));
}

// Jours retenus d'une sélection : valides, ouvrés, uniques, triés.
export function joursRetenus(dates) {
  return [...new Set((Array.isArray(dates) ? dates : []).filter((d) => dateValide(d) && !estWeekEnd(d)))].sort();
}
// Jours de week-end présents dans la sélection (avertissement).
export function joursWeekEnd(dates) {
  return [...new Set((Array.isArray(dates) ? dates : []).filter((d) => dateValide(d) && estWeekEnd(d)))].sort();
}
function slotsDesJours(choix) {
  const mode = choix?.mode;
  const out = [];
  for (const date of joursRetenus(choix.dates)) {
    if (mode === "HEURE") decouperHoraire(choix.heureDebut, choix.heureFin).forEach((p) => out.push({ date, ...p }));
    else if (mode === "AM" || mode === "PM") out.push({ date, periode: mode, heureDebut: null, heureFin: null });
    else if (mode === "JOUR") {
      out.push({ date, periode: "AM", heureDebut: null, heureFin: null });
      out.push({ date, periode: "PM", heureDebut: null, heureFin: null });
    }
  }
  return out;
}

// ─── Sélecteur de jours (§3) ───────────────────────────────────
// Raccourci « Remplir à partir de cette date » : n jours OUVRÉS à partir de
// dateISO (un départ en week-end glisse au lundi).
export function remplirJoursOuvres(dateISO, n) {
  const out = [];
  if (!dateValide(dateISO)) return out;
  let date = dateISO;
  while (estWeekEnd(date)) date = jourSuivant(date);
  const total = Math.max(1, Math.floor(Number(n) || 1));
  while (out.length < total) {
    out.push(date);
    do { date = jourSuivant(date); } while (estWeekEnd(date));
  }
  return out;
}
// Jours nécessaires pour n demi-journées (Journée = 2 demi-journées).
export function joursPourDemiJournees(n) {
  return Math.max(1, Math.ceil(Math.max(1, Math.floor(Number(n) || 1)) / 2));
}
// Bascule d'un jour dans la sélection (tri chronologique).
export function basculerJour(dates, iso) {
  const l = Array.isArray(dates) ? dates : [];
  return l.includes(iso) ? l.filter((d) => d !== iso) : [...l, iso].sort();
}

const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
export const MOIS_LONGS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
// « 3 jours — mar. 4, mer. 5, jeu. 6 nov. · 8 h–12 h ». Le mois suit le
// dernier jour de chaque mois. Aucun jour retenu → "".
export function resumeSelectionJours(dates, { mode, heureDebut, heureFin } = {}) {
  const jours = joursRetenus(dates);
  if (!jours.length) return "";
  const morceaux = jours.map((iso, k) => {
    const mois = iso.slice(0, 7);
    const dernierDuMois = k === jours.length - 1 || jours[k + 1].slice(0, 7) !== mois;
    return dernierDuMois ? `${libelleJour(iso)} ${MOIS_COURTS[Number(iso.slice(5, 7)) - 1]}` : libelleJour(iso);
  });
  let quand = "";
  if (mode === "HEURE") quand = libelleHoraire(heureDebut, heureFin);
  else if (mode === "AM") quand = "matin";
  else if (mode === "PM") quand = "après-midi";
  else if (mode === "JOUR") quand = "journée";
  return `${jours.length} jour${jours.length > 1 ? "s" : ""} — ${morceaux.join(", ")}${quand ? ` · ${quand}` : ""}`;
}

// Grille d'un mois (lundi en tête) : semaines de 7 cases, null hors mois.
// `mois` 1..12. → [[iso|null × 7], …]
export function grilleMois(annee, mois) {
  const premier = new Date(Date.UTC(annee, mois - 1, 1));
  const nb = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  const decalage = (premier.getUTCDay() + 6) % 7;
  const cases = Array(decalage).fill(null);
  for (let d = 1; d <= nb; d++) cases.push(`${annee}-${String(mois).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  while (cases.length % 7) cases.push(null);
  const out = [];
  for (let k = 0; k < cases.length; k += 7) out.push(cases.slice(k, k + 7));
  return out;
}
export function moisVoisin(annee, mois, delta) {
  const m = mois - 1 + delta;
  return { annee: annee + Math.floor(m / 12), mois: ((m % 12) + 12) % 12 + 1 };
}

// Plafond d'une mise au planning (UNE transaction) : 20 jours × 10
// personnes. → message d'erreur, ou "" si dans les clous.
export const PLAFOND_JOURS = 20;
export const PLAFOND_PERSONNES = 10;
export function controlerPlafond({ jours, personnes }) {
  if ((Number(jours) || 0) > PLAFOND_JOURS) {
    return `${PLAFOND_JOURS} jours au plus par mise au planning : retirez des jours, puis planifiez le reste en une seconde fois.`;
  }
  if ((Number(personnes) || 0) > PLAFOND_PERSONNES) {
    return `${PLAFOND_PERSONNES} intervenants au plus par mise au planning : planifiez les autres en une seconde fois.`;
  }
  return "";
}

// « client · travaux » ; en mode lignes « client · libellé de la ligne ».
// ≤ 80 caractères.
export function libellePoste(intervention, ligneDetail = null) {
  const client = String(intervention?.client?.nom || "").trim();
  const objet = String((ligneDetail ? ligneDetail.libelle : intervention?.travaux) || "").trim();
  const l = [client, objet].filter(Boolean).join(" · ");
  return l.length > LABEL_MAX ? `${l.slice(0, LABEL_MAX - 1)}…` : l;
}

// Une ligne de créneau. Pas de chantier, pas de poste, pas de tâche :
// `tempsEstimeH` = durée horaire, sinon null (défaut demi-journée).
// `ligneDetail` (mode lignes) : la ligne porte `interventionLigneId` =
// detail[].id et son libellé ; sinon `interventionLigneId` null (bloc).
export function ligneIntervention(intervention, slot, faireId, ligneDetail = null) {
  const d = enMinutes(slot?.heureDebut), f = enMinutes(slot?.heureFin);
  const horaire = d != null && f != null && f > d;
  return {
    id: faireId(),
    chantierId: null,
    batiment: null,
    posteAvancementKey: null,
    posteLabel: libellePoste(intervention, ligneDetail) || null,
    tempsEstimeH: horaire ? (f - d) / 60 : null,
    tacheRefId: null,
    interventionRefId: intervention?.id || null,
    interventionLigneId: ligneDetail?.id || null,
    heureDebut: horaire ? slot.heureDebut : null,
    heureFin: horaire ? slot.heureFin : null,
  };
}

// → [{ ressourceId, date, periode, ligne }] : une entrée par intervenant ×
// créneau. choix.ressourceIds = ids `utilisateurs` (ressourceId du planning).
export function lignesDepuisIntervention(intervention, choix, faireId) {
  const slots = slotsDepuisChoix(intervention, choix);
  const ids = [...new Set((choix?.ressourceIds || []).filter(Boolean))];
  const out = [];
  for (const ressourceId of ids) {
    for (const s of slots) {
      out.push({ ressourceId, date: s.date, periode: s.periode, ligne: ligneIntervention(intervention, s, faireId) });
    }
  }
  return out;
}

// Mode « Ligne par ligne » : planLignes = [{ ligneId, ressourceIds, date,
// mode, heureDebut, heureFin }], une entrée par ligne du détail retenue.
// Une ligne sans personne ou sans créneau est simplement ignorée (elle
// reste « non planifiée »). → [{ ressourceId, date, periode, ligne }]
export function lignesDepuisPlanLignes(intervention, planLignes, faireId) {
  const detail = Array.isArray(intervention?.detail) ? intervention.detail : [];
  const out = [];
  for (const pl of Array.isArray(planLignes) ? planLignes : []) {
    const ld = detail.find((d) => d?.id && d.id === pl?.ligneId);
    if (!ld) continue;
    const slots = slotsDepuisChoix(intervention, pl, ld);
    const ids = [...new Set((pl?.ressourceIds || []).filter(Boolean))];
    for (const ressourceId of ids) {
      for (const s of slots) {
        out.push({ ressourceId, date: s.date, periode: s.periode, ligne: ligneIntervention(intervention, s, faireId, ld) });
      }
    }
  }
  return out;
}

// ─── Fusion et conflits ────────────────────────────────────────
const memeHoraire = (a, b) => (a?.heureDebut || null) === (b?.heureDebut || null) && (a?.heureFin || null) === (b?.heureFin || null);

// Ajoute en FIN, ne mute rien. Même intervention, même ligne du détail
// (ou bloc) et même horaire → deja.
export function fusionnerLigneIntervention(existantes, nouvelle) {
  const lignes = Array.isArray(existantes) ? existantes.filter(Boolean) : [];
  const ref = nouvelle?.interventionRefId || null;
  const ligneId = nouvelle?.interventionLigneId || null;
  if (ref && lignes.some((l) => l.interventionRefId === ref && (l.interventionLigneId || null) === ligneId && memeHoraire(l, nouvelle))) {
    return { lignes: [...lignes], deja: true };
  }
  return { lignes: [...lignes, nouvelle], deja: false };
}

function intervalle(ligne, periode) {
  const d = enMinutes(ligne?.heureDebut), f = enMinutes(ligne?.heureFin);
  if (d != null && f != null && f > d) return [d, f];
  return periode === "PM" ? [MIDI, 24 * 60] : [0, MIDI];
}
// occupe : lignes SANS horaire déjà présentes (la demi-journée est prise) ;
// chevauchement : lignes AVEC horaire dont l'intervalle recoupe celui de la
// nouvelle (une nouvelle ligne sans horaire couvre toute la demi-journée).
// Les lignes de la même intervention sont ignorées (cas `deja`).
export function conflits(existantes, nouvelle, periode) {
  const autres = (Array.isArray(existantes) ? existantes : [])
    .filter((l) => l && !(nouvelle?.interventionRefId && l.interventionRefId === nouvelle.interventionRefId));
  const [a, b] = intervalle(nouvelle, periode);
  const occupe = autres.filter((l) => !heureValide(l.heureDebut) || !heureValide(l.heureFin));
  const chevauchement = autres.filter((l) => {
    if (!heureValide(l.heureDebut) || !heureValide(l.heureFin)) return false;
    const [c, d] = intervalle(l, periode);
    return c < b && a < d;
  });
  return { occupe, chevauchement };
}

// Retrait d'UNE ligne d'intervention d'un créneau (les autres intactes).
export function retirerLigneIntervention(lignes, interventionId, ligneId) {
  return (Array.isArray(lignes) ? lignes : [])
    .filter((l) => !(l && l.interventionRefId === interventionId && l.id === ligneId));
}

// Lignes d'un créneau qui portent l'intervention.
export function lignesDeLIntervention(creneau, interventionId) {
  const taches = Array.isArray(creneau?.taches) ? creneau.taches : [];
  return taches.filter((l) => l && l.interventionRefId === interventionId);
}

// ─── « Le retirer du chantier » (§3.7.2) ───────────────────────
// Lignes CHANTIER d'un créneau : portent un chantier et ne sont pas une
// ligne d'intervention.
export function lignesChantier(lignes) {
  return (Array.isArray(lignes) ? lignes : []).filter((l) => l && l.chantierId && !l.interventionRefId);
}
// Retire du créneau chacune de ses lignes chantier, une par une, avec
// `deplacer` (= deplacerVersPool du planning, injecté : ce module reste
// pur et testable sans lui). Ne mute rien.
// → { taches: lignes restantes, lignesPool: [ligne déplacée, …] }
export function retirerLignesChantier(creneau, deplacer) {
  let courant = { ...(creneau || {}), taches: Array.isArray(creneau?.taches) ? creneau.taches.filter(Boolean) : [] };
  const lignesPool = [];
  for (const l of lignesChantier(courant.taches)) {
    const r = deplacer(courant, l.id);
    if (!r || !r.lignePool) continue;
    lignesPool.push(r.lignePool);
    courant = r.creneauSansLigne;
  }
  return { taches: Array.isArray(courant.taches) ? courant.taches : [], lignesPool };
}

// ─── Avancement « ligne par ligne » (§3.7.1) ───────────────────
// Ids des lignes du détail présentes au planning pour CETTE intervention
// (calcul pur depuis les créneaux lus).
export function ligneIdsPlanifiees(creneaux, interventionId) {
  const out = new Set();
  for (const cr of Array.isArray(creneaux) ? creneaux : []) {
    for (const l of Array.isArray(cr?.taches) ? cr.taches : []) {
      if (l && l.interventionRefId === interventionId && l.interventionLigneId) out.add(l.interventionLigneId);
    }
  }
  return out;
}
// → { total N, planifiees n, parLigne: [{ id, libelle, demiJournees, nb }] }
// nb = nombre de lignes de planning (personne × demi-journée) de la ligne.
export function avancementLignes(intervention, creneaux) {
  const detail = Array.isArray(intervention?.detail) ? intervention.detail.filter((d) => d?.id) : [];
  const nb = new Map(detail.map((d) => [d.id, 0]));
  for (const cr of Array.isArray(creneaux) ? creneaux : []) {
    for (const l of Array.isArray(cr?.taches) ? cr.taches : []) {
      if (l && l.interventionRefId === intervention?.id && nb.has(l.interventionLigneId)) {
        nb.set(l.interventionLigneId, nb.get(l.interventionLigneId) + 1);
      }
    }
  }
  const parLigne = detail.map((d) => ({ id: d.id, libelle: d.libelle, demiJournees: d.demiJournees, nb: nb.get(d.id) }));
  return { total: detail.length, planifiees: parLigne.filter((x) => x.nb > 0).length, parLigne };
}
export function libelleAvancementLignes(a) {
  if (!a || !a.total) return "";
  return `${a.planifiees} ligne${a.planifiees > 1 ? "s" : ""} planifiée${a.planifiees > 1 ? "s" : ""} sur ${a.total}`;
}

// ─── Bannière conducteur (§3.7.3) ──────────────────────────────
// Créneaux POOL nés d'un « Le retirer du chantier » (champ de tête
// `issuDeIntervention`) et pas encore réaffectés (ressourceId nul).
export function creneauxAReaffecter(creneaux) {
  return (Array.isArray(creneaux) ? creneaux : [])
    .filter((c) => c && c.issuDeIntervention && !c.ressourceId)
    .sort((a, b) => `${a.date || ""}${a.periode || ""}`.localeCompare(`${b.date || ""}${b.periode || ""}`));
}
// → null | { count, unique } (unique = le seul créneau, pour le nommer)
export function notificationReaffectation(creneaux) {
  const l = creneauxAReaffecter(creneaux);
  if (!l.length) return null;
  return { count: l.length, unique: l.length === 1 ? l[0] : null };
}
